import fetcher_v2 from "@/api/fetcher_v2";
import * as Sentry from "@sentry/browser";

import { URLS } from "../urls";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

// Per-session cache so UTMs captured on the landing page carry through to
// subsequent events, even after the user navigates away from the URL that
// contained them.
const UTM_SESSION_STORAGE_KEY = "allears.tracking.utm";

const readStoredUtms = (): Record<string, string> => {
  try {
    const raw = window.sessionStorage.getItem(UTM_SESSION_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const getUtmParams = (): Record<string, string> => {
  if (typeof window === "undefined") return {};

  const fromUrl: Record<string, string> = {};
  try {
    const params = new URLSearchParams(window.location.search);
    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value) fromUrl[key] = value;
    }
  } catch {
    // URLSearchParams can theoretically throw on exotic input; fall through.
  }

  if (Object.keys(fromUrl).length > 0) {
    try {
      window.sessionStorage.setItem(
        UTM_SESSION_STORAGE_KEY,
        JSON.stringify(fromUrl)
      );
    } catch {
      // sessionStorage may be unavailable (e.g. privacy mode). Non-fatal.
    }
    return fromUrl;
  }

  return readStoredUtms();
};

// for future typescript:  { event: string, properties: Object}
export const postTrackingData = (
  event: string,
  properties: Record<string, unknown> = {}
) => {
  const propertiesWithScreenResolution = {
    ...{
      screen: {
        width: window.screen.width,
        height: window.screen.height,
        density: window.devicePixelRatio,
      },
    },
    ...getUtmParams(),
    ...properties,
  };

  return fetcher_v2(URLS.TRACK, {
    method: "POST",
    body: { event, properties: propertiesWithScreenResolution },
    // tracking is something of an edge-case when it comes to error handling.
    // ideally the user should never be affected, we just want to swallow any errors and pass them to sentry for debugging.
  }).catch((err) => {
    Sentry.captureException(err);
  });
};
